import type { NarrativeLocaleTableV0 } from './messages-ko';

export const NARRATIVE_MESSAGES_EN_V0: NarrativeLocaleTableV0 = {
  'home.hero.category':
    'The operating system that connects everything that happens in your company',
  'home.hero.promise':
    'Work that happens in your company — orders, purchasing, production, approvals — becomes a task for the right person, and the outcome and evidence stay on record.',
  'home.hero.differentiator':
    'ERP records work after it is finished. Inflomatrix creates the next task to do, and connects the outcome back to your company record.',
  'home.hero.ctaPrimary': 'Start your organization',
  'home.hero.ctaSecondary': 'See the real flow',
  'home.hero.inviteHint':
    'Only organizations with an invite code can start — confirmed during onboarding.',
  'home.breakPoints.eyebrow': 'What keeps repeating on the ground',
  'home.breakPoints.title':
    "If you keep asking people 'did this get handled?', this page is for you",
  'home.breakPoints.intro':
    'Orders come in by chat, get copied into a spreadsheet, and completion gets announced separately — the more tools you add, the more the connection breaks.',
  'home.oneFlow.eyebrow': 'One connected flow',
  'home.oneFlow.title':
    'When an order lands on the site, a task appears in the assignee’s workbench',
  'home.oneFlow.intro':
    'For organizations that run a customer-facing site alongside internal handling, we show — as an invite-only pilot — the moment an order becomes a task in the assignee’s workbench.',
  'home.proof.eyebrow': 'Evidence',
  'home.proof.title': 'The customer screen and the operating screen connect through one flow',
  'home.proof.intro':
    'The screenshots below were captured from a single new guest order on demo.staging.inflomatrix.app. The status is received — we do not claim a fulfilled screenshot until it is captured separately.',
  'home.proof.scope':
    'What a visitor sees here is one proven segment — order to workbench to received confirmation — from an invite-only pilot. We do not generalize this to every industry or every intake form.',
  'home.moreThanOrders.eyebrow': 'More than an order',
  'home.moreThanOrders.title': 'A storefront takes orders. Inflomatrix handles and records them.',
  'home.moreThanOrders.body':
    'ERP records work after it is finished. Inflomatrix creates the next task, hands it to the assignee, and writes the outcome back to your company record.',
  'home.threeChanges.eyebrow': 'Three things that change',
  'home.threeChanges.title': 'How an order becomes a record',
  'home.toolComparison.eyebrow': 'Comparison',
  'home.toolComparison.title': 'Taking an order is different from handling and recording it',
  'home.toolComparison.footnote':
    'Pilot scope: product detail → guest order → assignee workbench. We do not claim this is identical across every form or every industry.',
  'home.sharedRecord.eyebrow': 'One shared record',
  'home.sharedRecord.title':
    'Work that comes in from the site continues into the internal workbench',
  'home.sharedRecord.intro':
    'Creating one organization connects the customer-facing site and the internal workbench on the same record. Organizations that do not need a site (e.g. internal-only teams) can start with the workbench alone.',
  'home.startNow.eyebrow': 'What you can start today',
  'home.startNow.title': 'What is ready today, and what we prepare together',
  'home.startNow.subtitle':
    'We separate what the evidence above already shows from what we build together next.',
  'home.startNow.now.label': 'Ready to use today',
  'home.startNow.prepare.label': 'We prepare this together for your company',
  'home.startNow.notYet.label': 'Not yet',
  'home.ahead.eyebrow': 'Where we are headed',
  'home.ahead.title':
    'We prove the loop first, then build deeper operating intelligence on top of it',
  'home.ahead.body':
    'What we have proven now is the flow where an order becomes a task for the assignee and the outcome stays on record. Next, machines will notice repeating work first and suggest it to people, and eventually people and machines will work together on the same record. What this page shows today is that starting point.',
  'home.finalCta.title': 'Start your organization now',
  'home.finalCta.body':
    'If you have an invite code, you can start right away. If you want to see the real flow first, check the evidence.',
  'home.finalCta.ctaPrimary': 'Start your organization',
  'home.finalCta.ctaSecondary': 'See the real flow',
  'capabilities.connectOperations.label': 'Connect operations',
  'capabilities.connectOperations.problem': 'Orders, tasks, and handling break across tools.',
  'capabilities.connectOperations.prepares':
    'Prepares site, workbench, and operation modules on one connected flow.',
  'capabilities.connectOperations.example': 'Guest order continues into an assignee workbench loop',
  'capabilities.trustNumbers.label': 'Trust the numbers',
  'capabilities.trustNumbers.problem': 'Books and field quantities disagree.',
  'capabilities.trustNumbers.prepares':
    'Places accounting and quantity records on the same operating flow.',
  'capabilities.trustNumbers.example':
    'Receiving, inventory, and accounting convergence (staging-proven)',
  'capabilities.authorityEvidence.label': 'Authority and evidence',
  'capabilities.authorityEvidence.problem': 'It is hard to explain later who executed and why.',
  'capabilities.authorityEvidence.prepares':
    'Prepares propose → authority → evidence → execution grammar for the organization.',
  'capabilities.authorityEvidence.example': 'Approval records separated from execution receipts',
  'capabilities.physicalContext.label': 'Field connection',
  'capabilities.physicalContext.problem':
    'Field locations and objects are not connected to the system.',
  'capabilities.physicalContext.prepares':
    'Prepares connecting items and places through field scans.',
  'capabilities.physicalContext.example': 'Connect warehouse items and places by scan',
  'capabilities.temporalContext.label': 'Schedule connection',
  'capabilities.temporalContext.problem': 'Plans for when to do what do not stay in the system.',
  'capabilities.temporalContext.prepares':
    'Prepares connecting recurring schedules with regular work.',
  'capabilities.temporalContext.example': 'Recurring work that becomes tasks on schedule',
  'capabilities.governedAi.label': 'Governed AI',
  'capabilities.governedAi.problem':
    'AI proposals are risky if they execute without authority and evidence.',
  'capabilities.governedAi.prepares':
    'Lets AI analyze, propose, and execute only within governed bounds.',
  'capabilities.governedAi.example':
    'Multi-agent long-running work (staging; not unbounded autonomy)',
  'capabilities.humanoidVision.label': 'Humanoid vision',
  'capabilities.humanoidVision.problem': 'Future physical actors need the same operating language.',
  'capabilities.humanoidVision.prepares':
    'Described only as vision or plan for now. Does not claim real-world execution.',
  'capabilities.humanoidVision.example': 'Action context and digital twin (context-only)',
  'claim.qualifier.proven': 'Proven',
  'claim.qualifier.preview': 'Preview',
  'claim.qualifier.planned': 'Planned',
  'claim.qualifier.vision': 'Vision',
  'maturity.current': 'Current',
  'maturity.nearTerm': 'Near-term',
  'maturity.longTerm': 'Long-term',
  'genesis.stage1.title': 'First, let’s understand your organization',
  'genesis.stage1.subtitle':
    'Choose scale and operating form, and we will adapt the rest. Your inputs prepare draft site and workbench materials automatically.',
  'genesis.stage1.cta.primary': 'Continue',
  'genesis.stage1.cta.secondary': 'Not sure — get a recommendation',
  'genesis.stage1.trust': 'You can change these choices later; nothing here is final yet.',
  'genesis.stage2.title': 'Upload what you already have to reduce manual entry',
  'genesis.stage2.subtitle':
    'Company intro, existing site URL, or product list — anything helps. Imperfect is fine; give only what you have.',
  'genesis.stage2.trust':
    'Uploaded materials are used for drafts only; nothing is published before you confirm.',
  'genesis.stage2.claimBoundary':
    'Auto-extraction is a draft — you will confirm it in the next step.',
  'genesis.stage2.source.url': 'Existing site URL',
  'genesis.stage2.source.file': 'Company intro / document (filename note)',
  'genesis.stage2.source.catalog': 'Product / service list hints',
  'genesis.stage2.source.optional': 'All fields are optional. You can continue with blanks.',
  'genesis.stage3.title': 'We found these facts. Confirm or correct them',
  'genesis.stage3.trust':
    'AI-filled values show a gray badge. Until you confirm, they remain estimates.',
  'genesis.stage3.claimBoundary':
    'Auto-fill is controlled assistive scope — not unbounded autonomy.',
  'genesis.stage3.badge.ai': 'AI suggestion · source: {source}',
  'genesis.stage3.badge.confirmed': 'Confirmed',
  'genesis.stage3.empty': 'No extracted items to confirm yet. Continue entry or add sources.',
  'genesis.stage4.title': 'We recommend operating capabilities for this organization',
  'genesis.stage4.subtitle':
    'Proposals cover mountains A–F only. You can defer or open details if the fit is wrong.',
  'genesis.stage4.why': 'Why recommended: based on the scale and operating form you entered',
  'genesis.stage4.cta.accept': 'Accept recommendation',
  'genesis.stage4.cta.later': 'Later',
  'genesis.stage4.cta.details': 'View details',
  'genesis.stage4.minimumFloor':
    'Connect operations and authority/evidence are included as the minimum floor.',
  'genesis.stage5.title': 'You can start now',
  'genesis.stage5.subtitle':
    'Some items are empty, but insufficient information is not a configuration failure. Review the gap classes.',
  'genesis.stage5.trust':
    'Empty fields still allow organization creation. Publishing happens only when you choose.',
  'genesis.stage5.insufficientInfo': 'Insufficient information is not a configuration failure.',
  'genesis.stage5.gap.mustResolve': 'This blocks first work, so resolve it now.',
  'genesis.stage5.gap.useDefault': 'You can proceed now using the recommended default.',
  'genesis.stage5.gap.configureLater': 'You can configure this later in Business screens.',
  'genesis.stage5.gap.notNeeded': 'Not needed for first operation.',
  'genesis.stage5.gap.insufficientInfo': 'Insufficient information is not a configuration failure.',
  'genesis.stage6.title': 'We will create the organization with this. Review once before creating',
  'genesis.stage6.trust': 'Nothing is created until you confirm.',
  'genesis.stage6.irreversible':
    'The organization address ({org}.inflomatrix.app) will be finalized.',
  'genesis.stage6.cta.primary': 'Create organization',
  'genesis.stage6.cta.secondary': 'Edit previous step',
  'genesis.stage6.item.workspace': 'Workspace and organization address',
  'genesis.stage6.item.flows': 'Operating flows (Flow)',
  'genesis.stage6.item.objects': 'Work objects (Object)',
  'genesis.stage6.item.authority': 'Authority and approvals',
  'genesis.stage6.item.evidence': 'Evidence',
  'genesis.stage6.item.aiScope': 'AI scope (controlled assist)',
  'genesis.stage6.item.excluded': 'Excluded from this creation',
  'genesis.stage7.title': 'Preparing your company operating structure',
  'genesis.stage7.subtitle': 'Progress is shown as user-value steps, not substrate jargon.',
  'genesis.stage7.trust': 'If a step fails, we tell you here immediately.',
  'genesis.stage7.step.workspace': 'Prepare workspace',
  'genesis.stage7.step.teamPermissions': 'Prepare team and permissions',
  'genesis.stage7.step.workflows': 'Prepare operating workflows',
  'genesis.stage7.step.docsEvidence': 'Prepare documents and evidence',
  'genesis.stage7.step.firstWork': 'Prepare first work',
  'genesis.stage7.step.verify': 'Confirm completion',
  'genesis.stage8.title': 'Ready for first operation',
  'genesis.stage8.subtitle':
    'Review the recommended first work, objects/sources, role, evidence, and start CTA. Later items are listed separately.',
  'genesis.stage8.finishLine': 'FIRST_OPERATION_READY',
  'genesis.stage8.cta.primary': 'Start first work',
  'genesis.stage8.cta.secondary': 'View items to configure later',
  'genesis.stage8.laterItems':
    'Public site, detailed permissions, and extra modules can be configured later.',
  'genesis.stage8.humanoidSecondary':
    'Future physical-actor (humanoid) narrative is secondary and is not a condition for first work now.',
  'genesis.stage8.firstOp.procurementReceiving.why':
    'Receiving and inspection fit your input signals well.',
  'genesis.stage8.firstOp.procurementReceiving.objects': 'Purchase orders and receiving items',
  'genesis.stage8.firstOp.procurementReceiving.role': 'Receiving assignee',
  'genesis.stage8.firstOp.procurementReceiving.evidence': 'Quantity and inspection evidence',
  'genesis.stage8.firstOp.catalogOrder.why': 'Catalog order loop fits your input signals well.',
  'genesis.stage8.firstOp.catalogOrder.objects': 'Products and guest orders',
  'genesis.stage8.firstOp.catalogOrder.role': 'Order handling assignee',
  'genesis.stage8.firstOp.catalogOrder.evidence': 'Order received receipt',
  'genesis.stage8.firstOp.general.why':
    'We prepare general first work from your organization basics.',
  'genesis.stage8.firstOp.general.objects': 'Organization and workbench defaults',
  'genesis.stage8.firstOp.general.role': 'Organization admin',
  'genesis.stage8.firstOp.general.evidence': 'Configuration completion record',
  'genesis.stage8.firstOp.procurementReceiving.label': 'Receiving and inspection',
  'genesis.stage8.firstOp.catalogOrder.label': 'Order handling',
  'genesis.stage8.firstOp.general.label': 'Prepare first organization task',
  'genesis.adaptive.dontKnow': 'Not sure — get a recommendation',
  'gap.class.MUST_RESOLVE_NOW': 'Must resolve now',
  'gap.class.USE_RECOMMENDED_DEFAULT': 'Use recommended default',
  'gap.class.CONFIGURE_LATER': 'Configure later',
  'gap.class.NOT_NEEDED_FOR_FIRST_OPERATION': 'Not needed for first operation',
};
